<script setup lang="ts">
import { PlusOutlined } from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { computed, h, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { Guest } from '@/models'
import { createGuest, deleteGuest, fetchGuests, updateGuest } from '@/services/guests'
import { useAuthStore } from '@/stores/auth'
import GuestFormDialog from './guests/GuestFormDialog.vue'

const router = useRouter()
const auth = useAuthStore()

const guests = ref<Guest[]>([])
const loading = ref(false)
const search = ref('')
const dialog = ref(false)
const editingGuest = ref<Guest | null>(null)

const filteredGuests = computed(() => {
  const q = search.value.toLowerCase()
  if (!q) return guests.value
  return guests.value.filter(
    (g) =>
      g.primary_name.toLowerCase().includes(q) ||
      g.contact_number?.toLowerCase().includes(q) ||
      g.email?.toLowerCase().includes(q),
  )
})

const canAdd = computed(() => auth.can('guests', 'add'))

const loadData = async () => {
  loading.value = true
  try {
    const companyId = auth.isAdmin ? undefined : (auth.user?.company_id as string | undefined)
    guests.value = await fetchGuests(companyId)
  } catch (error: any) {
    message.error(error?.message || 'Failed to load guests.')
  } finally {
    loading.value = false
  }
}

const openNew = () => {
  if (!canAdd.value) {
    message.warning('No permission.')
    return
  }
  editingGuest.value = null
  dialog.value = true
}

const openEdit = (guest: Guest, e: Event) => {
  e.stopPropagation()
  editingGuest.value = { ...guest }
  dialog.value = true
}

const handleDelete = async (guest: Guest, e: Event) => {
  e.stopPropagation()
  try {
    await deleteGuest(guest.id)
    message.success('Guest deleted.')
    await loadData()
  } catch (error: any) {
    message.error(error?.message || 'Failed to delete guest.')
  }
}

const handleSave = async (payload: Partial<Guest>) => {
  try {
    if (editingGuest.value?.id) {
      await updateGuest(editingGuest.value.id, payload)
      message.success('Guest updated.')
    } else {
      await createGuest(payload)
      message.success('Guest created.')
    }
    dialog.value = false
    await loadData()
  } catch (error: any) {
    message.error(error?.message || 'Failed to save guest.')
  }
}

const viewDetail = (id: string) => {
  router.push(`/guests/${id}`)
}

const columns = [
  { title: 'Name', dataIndex: 'primary_name', key: 'primary_name', width: 200 },
  { title: 'Contact', dataIndex: 'contact_number', key: 'contact_number', width: 160 },
  { title: 'Email', dataIndex: 'email', key: 'email', width: 200 },
  {
    title: 'Adults',
    dataIndex: 'adults',
    key: 'adults',
    width: 80,
    align: 'center' as const,
  },
  {
    title: 'Children',
    dataIndex: 'children',
    key: 'children',
    width: 80,
    align: 'center' as const,
  },
  {
    title: 'Guest Names',
    key: 'guest_names',
    width: 200,
    customRender: ({ record }: { record: Guest }) => {
      const names = record.guest_names || []
      if (!names.length) return h('span', { style: { color: '#999' } }, '—')
      return names.join(', ')
    },
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 140,
    customRender: ({ record }: { record: Guest }) => {
      const canEdit = auth.can('guests', 'edit', record.company_id)
      const canDelete = auth.can('guests', 'delete', record.company_id)
      return h('a-space', [
        canEdit
          ? h('a-button', { size: 'small', onClick: (e: Event) => openEdit(record, e) }, 'Edit')
          : null,
        canDelete
          ? h(
              'a-popconfirm',
              {
                title: 'Delete Guest',
                description: 'Are you sure?',
                onConfirm: (e: Event) => handleDelete(record, e),
              },
              () => h('a-button', { size: 'small', danger: true }, 'Delete'),
            )
          : null,
      ])
    },
  },
]

onMounted(loadData)
</script>

<template>
  <a-flex style="margin-bottom: 16px" justify="space-between" gap="small" wrap="wrap" align="center">
    <a-input-search v-model:value="search" placeholder="Search guests..." style="max-width: 320px" />
    <a-space>
      <a-button @click="loadData" :loading="loading">Refresh</a-button>
      <a-button
        type="primary"
        :icon="h(PlusOutlined)"
        @click="openNew"
        :disabled="!canAdd"
      >
        New Guest
      </a-button>
    </a-space>
  </a-flex>

  <a-spin :spinning="loading">
    <div
      v-if="filteredGuests.length === 0"
      style="display: flex; justify-content: center; align-items: center; min-height: 300px"
    >
      <a-empty description="No guests found" />
    </div>
    <a-table
      v-else
      :columns="columns"
      :data-source="filteredGuests"
      :row-key="(r: Guest) => r.id"
      :pagination="{ pageSize: 20 }"
      size="middle"
      bordered
      @row-click="(record: Guest) => viewDetail(record.id)"
      :row-class-name="() => 'guest-row'"
    />
  </a-spin>

  <GuestFormDialog
      v-model:visible="dialog"
    :guest="editingGuest"
    @save="handleSave"
  />
</template>

<style scoped>
:deep(.guest-row) {
  cursor: pointer;
}
</style>
